import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Database, Shield, Users, TrendingUp, Sparkles, ArrowRight, X } from "lucide-react";
import AuroraBackground from "./AuroraBackground";

/**
 * MagicEnrichmentOverlay — "Magic Prosperr" wow moment.
 *
 * Lifecycle:
 *   1. Component mounts the moment enrichment starts → 4 sequential scan phases
 *      animate in (Pappers → SIRENE → dirigeants → synthèse), each ~800ms.
 *   2. The API call runs in parallel via `enrichPromise`.
 *   3. As soon as BOTH the min-animation window (3.2s) AND the API have resolved,
 *      the phases collapse and the result cards reveal:
 *        • Header card (company + form + capital + employees, with counters)
 *        • Revenue / Net result (counter up)
 *        • Top dirigeants
 *        • Score Prosperr (radial progress)
 *   4. User taps "Voir la fiche complète" → navigates.
 *
 * Props:
 *   - prospect       : { prospect_id, name, company, ... }
 *   - enrichPromise  : Promise resolving to { suggested, deep, applied, ... } | null
 *   - onClose()      : called when user dismisses or clicks the CTA
 *   - onViewProfile()
 */
const PHASES = [
  { id: "pappers", label: "Connexion à Pappers", sub: "Base officielle des entreprises", Icon: Database },
  { id: "sirene",  label: "Vérification SIRENE", sub: "Confirmation INSEE en cours…", Icon: Shield },
  { id: "leaders", label: "Récupération des dirigeants", sub: "Représentants légaux et mandats…", Icon: Users },
  { id: "synth",   label: "Synthèse financière",  sub: "CA, capital, effectifs, croissance…", Icon: TrendingUp },
];

const PHASE_MS = 800; // each phase ON-screen
const MIN_TOTAL_MS = PHASE_MS * PHASES.length;

function formatMoney(n) {
  if (n == null) return "—";
  const v = Math.round(n);
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M€`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1000).toFixed(0)} k€`;
  return `${v} €`;
}

/** Animates a number from 0 → target over `duration` ms. */
function useCountUp(target, duration = 900, trigger = true) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!trigger || target == null) return;
    let raf;
    const start = performance.now();
    const num = Number(target) || 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(num * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, trigger]);
  return value;
}

export default function MagicEnrichmentOverlay({ prospect, enrichPromise, onClose, onViewProfile }) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [done, setDone] = useState(null);          // API result
  const [showResult, setShowResult] = useState(false);
  const errorRef = useRef(null);

  /* Phase ticker */
  useEffect(() => {
    if (phaseIdx >= PHASES.length - 1) return;
    const t = setTimeout(() => setPhaseIdx((i) => i + 1), PHASE_MS);
    return () => clearTimeout(t);
  }, [phaseIdx]);

  /* Run promise + wait for min animation window before revealing */
  useEffect(() => {
    let mounted = true;
    const minDelay = new Promise((r) => setTimeout(r, MIN_TOTAL_MS + 300));
    (async () => {
      try {
        const [data] = await Promise.all([enrichPromise, minDelay]);
        if (!mounted) return;
        setDone(data);
        setPhaseIdx(PHASES.length - 1);
        setTimeout(() => mounted && setShowResult(true), 250);
      } catch (e) {
        errorRef.current = e?.response?.data?.detail || "Service indisponible";
        if (!mounted) return;
        setDone({ failed: true });
        setShowResult(true);
      }
    })();
    return () => { mounted = false; };
  }, [enrichPromise]);

  /* Result shape from API: { suggested: {...}, deep: {...}, applied: bool, ... } */
  const merged = useMemo(() => {
    if (!done || done.failed) return null;
    const s = done.suggested || {};
    const d = done.deep || {};
    return {
      company: prospect.company || s.company || s.denomination || "—",
      name: prospect.name || "—",
      siret: prospect.siret || s.siret || "",
      forme: d.legal_form || s.forme_juridique || s.legal_form || "",
      capital: Number(d.capital ?? s.capital ?? 0),
      employees: Number(d.employees ?? s.effectifs ?? s.employees ?? 0),
      revenue: Number(d.revenue ?? s.chiffre_affaires ?? s.revenue ?? 0),
      net: Number(d.net_result ?? s.resultat_net ?? 0),
      leaders: d.leaders || s.dirigeants || s.leaders || [],
      score: Math.min(100, Math.max(0, Number(d.score ?? prospect.score ?? 0))),
      newKeys: Object.keys(done.suggested || {}).length + (done.deep ? 1 : 0),
    };
  }, [done, prospect]);

  const score    = useCountUp(merged?.score || 0,     1100, showResult);
  const revenue  = useCountUp(merged?.revenue || 0,   1100, showResult);
  const capital  = useCountUp(merged?.capital || 0,    900, showResult);
  const employees= useCountUp(merged?.employees || 0,  900, showResult);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[9990] flex items-end sm:items-center justify-center" data-testid="magic-enrich-overlay">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md cursor-pointer"
        onClick={() => showResult && onClose?.()}
      />

      {/* Modal */}
      <div className="relative w-full sm:max-w-md sm:rounded-2xl bg-[var(--surface)] border-t sm:border border-[var(--border)] overflow-hidden shadow-floating max-h-[92vh] flex flex-col">
        <AuroraBackground intensity={0.55} className="absolute inset-0" />

        {/* Close (only after result) */}
        {showResult && (
          <button
            onClick={onClose}
            data-testid="magic-enrich-close"
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center text-zinc-300 transition-colors"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <div className="relative z-[1] flex-1 overflow-y-auto">
          {/* ═════════════ Header ═════════════ */}
          <div className="px-6 pt-7 pb-4 text-center">
            <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.4em] text-emerald-300 mb-3">
              <Sparkles className="w-3 h-3" strokeWidth={2.4} />
              Magic Prosperr
            </div>
            <h2 className="font-display text-2xl font-semibold text-white tracking-tight" data-testid="magic-target-name">
              {prospect.company || prospect.name || "Enrichissement"}
            </h2>
            <p className="text-[12px] text-zinc-400 mt-1">
              {showResult
                ? (merged && merged.newKeys > 0 ? "Synthèse terminée." : "Analyse complète.")
                : "Analyse en cours · Prosperr scanne les bases publiques"}
            </p>
          </div>

          {/* ═════════════ PHASE STREAM ═════════════ */}
          {!showResult && (
            <div className="px-6 pb-6 space-y-2.5" data-testid="magic-phases">
              {PHASES.map((ph, i) => {
                const state = i < phaseIdx ? "done" : i === phaseIdx ? "active" : "idle";
                const Icon = state === "done" ? CheckCircle2 : ph.Icon;
                return (
                  <div
                    key={ph.id}
                    className={`flex items-center gap-3 px-3.5 py-3 rounded-xl border transition-all duration-500 ${
                      state === "active"
                        ? "border-emerald-500/60 bg-emerald-500/12"
                        : state === "done"
                        ? "border-emerald-500/30 bg-emerald-500/5"
                        : "border-[var(--border)] bg-[var(--surface-alt)]/50 opacity-50"
                    }`}
                    style={{
                      transform: state === "idle" ? "translateY(0)" : "translateY(0)",
                      opacity: state === "idle" ? 0.4 : 1,
                    }}
                  >
                    <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${
                      state === "active"
                        ? "bg-emerald-500/30 text-emerald-200 magic-pulse"
                        : state === "done"
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "bg-[var(--surface-alt)] text-zinc-500"
                    }`}>
                      <Icon className="w-4 h-4" strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[13px] font-medium leading-tight ${
                        state === "done" ? "text-emerald-200" : state === "active" ? "text-white" : "text-zinc-400"
                      }`}>
                        {ph.label}
                      </div>
                      <div className="text-[10.5px] text-zinc-500 mt-0.5">{ph.sub}</div>
                    </div>
                    {state === "active" && (
                      <div className="flex gap-1 shrink-0">
                        <span className="w-1 h-1 rounded-full bg-emerald-300 magic-dot" style={{ animationDelay: "0ms" }} />
                        <span className="w-1 h-1 rounded-full bg-emerald-300 magic-dot" style={{ animationDelay: "160ms" }} />
                        <span className="w-1 h-1 rounded-full bg-emerald-300 magic-dot" style={{ animationDelay: "320ms" }} />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Scanning shimmer line */}
              <div className="mt-5 h-[2px] mx-auto w-full rounded-full bg-white/5 overflow-hidden">
                <div className="h-full magic-progress" />
              </div>
            </div>
          )}

          {/* ═════════════ RESULT CARDS ═════════════ */}
          {showResult && merged && !done?.failed && (
            <div className="px-5 pb-5 space-y-3" data-testid="magic-result">
              {/* Score radial — hero */}
              <div className="rounded-2xl border border-emerald-500/30 bg-[var(--surface)]/70 backdrop-blur p-5 flex items-center gap-5 magic-reveal" style={{ animationDelay: "0ms" }}>
                <ScoreRing value={score} target={merged.score} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-emerald-300 mb-1">Score Prosperr</div>
                  <div className="font-display text-2xl font-semibold text-white tabular-nums leading-tight">
                    {Math.round(score)}<span className="text-zinc-500 text-base">/100</span>
                  </div>
                  <div className="text-[11.5px] text-zinc-400 mt-1 leading-snug">
                    {merged.score >= 80
                      ? "Excellent lead · forte probabilité de conversion"
                      : merged.score >= 60
                      ? "Lead solide · à prioriser cette semaine"
                      : merged.score >= 40
                      ? "Lead correct · à qualifier"
                      : "Lead faible · vérifiez la pertinence"}
                  </div>
                </div>
              </div>

              {/* KPI duo */}
              <div className="grid grid-cols-2 gap-3 magic-reveal" style={{ animationDelay: "160ms" }}>
                <KpiTile label="Chiffre d'affaires" value={formatMoney(revenue)} live />
                <KpiTile label="Effectifs" value={Math.round(employees).toString()} live />
              </div>

              {/* Detail card */}
              <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)]/70 backdrop-blur p-4 space-y-2.5 magic-reveal" style={{ animationDelay: "320ms" }}>
                {merged.forme && (
                  <Row label="Forme juridique" value={merged.forme} />
                )}
                {merged.capital > 0 && (
                  <Row label="Capital social" value={<span className="tabular-nums">{formatMoney(capital)}</span>} />
                )}
                {merged.siret && (
                  <Row label="SIRET" value={<span className="font-mono text-[12px] text-zinc-200">{merged.siret}</span>} />
                )}
                {merged.net !== 0 && (
                  <Row label="Résultat net" value={<span className={`tabular-nums ${merged.net > 0 ? "text-emerald-300" : "text-rose-300"}`}>{formatMoney(merged.net)}</span>} />
                )}
              </div>

              {/* Leaders */}
              {(merged.leaders?.length || 0) > 0 && (
                <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)]/70 backdrop-blur p-4 magic-reveal" style={{ animationDelay: "480ms" }}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-2">Dirigeants</div>
                  <div className="space-y-1.5">
                    {merged.leaders.slice(0, 4).map((l, i) => {
                      const display = typeof l === "string" ? l : (l.name || `${l.first_name || ""} ${l.last_name || ""}`.trim());
                      const role = typeof l === "object" ? (l.role || l.qualite || "") : "";
                      return (
                        <div key={`${display}-${i}`} className="flex items-center gap-2.5 text-[12.5px]">
                          <div className="w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-500/30 grid place-items-center text-emerald-200 text-[10px] font-semibold shrink-0">
                            {(display || "?").charAt(0).toUpperCase()}
                          </div>
                          <span className="text-white truncate flex-1">{display || "—"}</span>
                          {role && <span className="text-[10px] text-zinc-500 uppercase tracking-wider shrink-0">{role}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {merged.newKeys === 0 && (
                <div className="text-center text-[12px] text-zinc-500 py-2 magic-reveal" style={{ animationDelay: "320ms" }}>
                  Aucune nouvelle donnée publique trouvée pour cette entreprise.
                </div>
              )}
            </div>
          )}

          {showResult && done?.failed && (
            <div className="px-6 pb-6 text-center magic-reveal">
              <div className="text-[13px] text-rose-300 mt-3">{errorRef.current || "Service indisponible"}</div>
            </div>
          )}
        </div>

        {/* Footer CTA */}
        {showResult && (
          <div className="relative z-[1] px-5 py-3 border-t border-[var(--border)] bg-[var(--bg)]/40 backdrop-blur grid grid-cols-2 gap-2 shrink-0">
            <button
              onClick={onClose}
              data-testid="magic-enrich-dismiss"
              className="hp-btn hp-btn-secondary h-11 text-[13px]"
            >
              Fermer
            </button>
            <button
              onClick={onViewProfile}
              data-testid="magic-enrich-view"
              className="hp-btn hp-btn-primary h-11 text-[13px] flex items-center justify-center gap-2"
            >
              Voir la fiche complète <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes magicPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(0,220,130,0.55); } 50% { box-shadow: 0 0 0 8px rgba(0,220,130,0); } }
        @keyframes magicDot { 0%,80%,100% { transform: scale(1); opacity: 0.5; } 40% { transform: scale(1.5); opacity: 1; } }
        @keyframes magicReveal { 0% { opacity: 0; transform: translateY(14px) scale(0.98); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes magicProgress { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }
        .magic-pulse { animation: magicPulse 1.4s ease-in-out infinite; }
        .magic-dot { animation: magicDot 1.1s ease-in-out infinite; display: inline-block; }
        .magic-reveal { animation: magicReveal 600ms cubic-bezier(.2,.7,.3,1) both; }
        .magic-progress { width: 40%; height: 100%; background: linear-gradient(90deg, transparent, rgba(0,220,130,1), rgba(217,70,239,1), transparent); animation: magicProgress 1.6s linear infinite; }
      `}</style>
    </div>,
    document.body
  );
}

/* ─── tiny sub-components ─── */
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-[12.5px]">
      <span className="text-zinc-500 uppercase text-[10px] tracking-[0.2em]">{label}</span>
      <span className="text-zinc-100 font-medium text-right">{value}</span>
    </div>
  );
}

function KpiTile({ label, value, live }) {
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)]/70 backdrop-blur p-4">
      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em]">{label}</div>
      <div className={`font-display text-xl font-semibold tabular-nums mt-1 ${live ? "text-white" : "text-zinc-200"}`}>{value}</div>
    </div>
  );
}

function ScoreRing({ value = 0, target = 0 }) {
  const r = 28;
  const c = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, value));
  const offset = c * (1 - pct / 100);
  const color =
    target >= 80 ? "#34D399" :
    target >= 60 ? "#A78BFA" :
    target >= 40 ? "#F59E0B" : "#F87171";
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" className="shrink-0">
      <circle cx="36" cy="36" r={r} stroke="rgba(255,255,255,0.07)" strokeWidth="6" fill="none" />
      <circle
        cx="36" cy="36" r={r}
        stroke={color}
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 36 36)"
        style={{ filter: `drop-shadow(0 0 8px ${color}66)`, transition: "stroke-dashoffset 1.1s cubic-bezier(.2,.7,.3,1)" }}
      />
    </svg>
  );
}
